import React from "react";
import { useSelector } from "react-redux";

import {
  AaaGameInformationGameExpectationsDataType,
  TargetPlatform,
} from "../../../../models/forms";
import { TargetPlatforms, TargetPlatformsDisplayName } from "../../../../models/targetPlatforms";
import { getMiddlewareEngines } from "../../../../redux/selectors";
import { FieldOption } from "../../../../server/crm/api/models/forms";
import { getDateStringForReadOnlyForm, ReadOnlyFormItem } from "../appInfoModelsAndHelpers";
import { ReadOnlyForm } from "../ReadOnlyForm";

type ApplicationInformationRowContentProps = {
  dataInForm: AaaGameInformationGameExpectationsDataType;
};

const mapTerritoryNameToTranslatedStringKey = (input: string): string =>
  ({
    asia: "aaaReleaseDateAsiaLabel",
    anz: "aaaReleaseDateAnzLabel",
    europe: "aaaReleaseDateEuropeLabel",
    japan: "aaaReleaseDateJapanLabel",
    northAmerica: "aaaReleaseDateNorthAmericaLabel",
    southAmerica: "aaaReleaseDateSouthAmericaLabel",
  })[input] || "";

const mapDemoTrialBetaToTranslatedStringKey = (input: string): string =>
  ({
    betas: "aaaBetaLabel",
    demos: "aaaDemoLabel",
    trials: "aaaTrialLabel",
  })[input] || "";

const AaaGameExpectationsConfig: Map<string, ReadOnlyFormItem> = new Map([
  [
    "releaseDatesByTerritory",
    {
      title: "aaaGameExpReleaseDatesTitle",
      mapper: (
        data: AaaGameInformationGameExpectationsDataType,
        localizedStrings: Map<string, string>
      ) =>
        Object.keys(data.releaseDatesByTerritory).map(territoryName => {
          const territory = data.releaseDatesByTerritory[territoryName];
          return territory.isSelected
            ? `${localizedStrings.get(mapTerritoryNameToTranslatedStringKey(territoryName))}:
          ${getDateStringForReadOnlyForm(territory.releaseDate)}`
            : null;
        }),
    },
  ],
  [
    "demosTrialsBetas",
    {
      title: "aaaGameExpDemosTitle",
      mapper: (
        data: AaaGameInformationGameExpectationsDataType,
        localizedStrings: Map<string, string>
      ) =>
        Object.keys(data.demosTrialsBetas).map(key => {
          const demoTrialBeta = data.demosTrialsBetas[key];
          return demoTrialBeta.isSelected
            ? `${localizedStrings.get(
                mapDemoTrialBetaToTranslatedStringKey(key)
              )}: ${getDateStringForReadOnlyForm(demoTrialBeta.releaseDate)}`
            : null;
        }),
    },
  ],
  [
    "targetPlatforms",
    {
      title: "targetSystemsAndPlatforms",
      mapper: (
        data: AaaGameInformationGameExpectationsDataType,
        localizedStrings: Map<string, string>
      ) =>
        data.targetPlatforms
          .filter(function (x: { selected: boolean }) {
            return x.selected;
          })
          .map((item: TargetPlatform) => {
            let platformName: string | undefined = "";
            switch (item.platform) {
              case TargetPlatforms.windowsStore:
                platformName = localizedStrings.get("microsoftStoreOnWindowsPlatform");
                break;
              case TargetPlatforms.other:
                platformName = item.otherPlatformName;
                break;
              default:
                platformName = TargetPlatformsDisplayName[item.platform];
                break;
            }
            return `${platformName} : ${getDateStringForReadOnlyForm(item.releaseDate)}`;
          }),
    },
  ],
  [
    "middleware",
    {
      title: "middlewareEngine",
      mapper: (
        data: AaaGameInformationGameExpectationsDataType,
        _localizedStrings: Map<string, string>,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        additionalData?: Map<string, any>
      ) => {
        if (!additionalData) {
          return null;
        }
        const crmMiddlewareEnginesList = additionalData.get(
          "crmMiddlewareEnginesList"
        ) as FieldOption[];
        return crmMiddlewareEnginesList.find(
          middlewareItem => middlewareItem.id === data.middleware
        )?.value;
      },
    },
  ],
  [
    "middlewareVersion",
    {
      title: "currentVersion",
    },
  ],
  [
    "middlewareNotes",
    {
      title: "additionalMiddlewareNotes",
    },
  ],
  ["releaseNotes", {}],
]);

export const ReadOnlyAaaGameExpectations = ({
  dataInForm,
}: ApplicationInformationRowContentProps) => {
  const extraStringsToLocalize = [
    "aaaReleaseDateNorthAmericaLabel",
    "aaaReleaseDateEuropeLabel",
    "aaaReleaseDateJapanLabel",
    "aaaReleaseDateAsiaLabel",
    "aaaReleaseDateAnzLabel",
    "aaaReleaseDateSouthAmericaLabel",
    "aaaBetaLabel",
    "aaaDemoLabel",
    "aaaTrialLabel",
    "microsoftStoreOnWindowsPlatform",
  ];

  const crmMiddlewareEnginesList = useSelector(getMiddlewareEngines);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const additionalData = new Map<string, any>([
    ["crmMiddlewareEnginesList", crmMiddlewareEnginesList],
  ]);

  return (
    <ReadOnlyForm
      formConfig={AaaGameExpectationsConfig}
      formData={dataInForm}
      extraStringsToLocalize={extraStringsToLocalize}
      additionalData={additionalData}
    />
  );
};
